import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Clock, MapPin, Users, ArrowRight, ChevronRight, Ban, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

function statusColor(s) {
    if (s === "booked") return "bg-[hsl(var(--status-new)/0.18)] text-foreground border-[hsl(var(--status-new)/0.3)]";
    if (s === "checked_in") return "bg-[hsl(var(--status-preparing)/0.18)] text-foreground border-[hsl(var(--status-preparing)/0.3)]";
    if (s === "seated") return "bg-[hsl(var(--status-ready)/0.18)] text-foreground border-[hsl(var(--status-ready)/0.3)]";
    if (s === "completed") return "bg-[hsl(var(--status-served)/0.18)] text-foreground border-[hsl(var(--status-served)/0.3)]";
    if (s === "cancelled") return "bg-[hsl(var(--status-cancelled)/0.18)] text-foreground border-[hsl(var(--status-cancelled)/0.3)]";
    return "";
}

export default function ReservationsPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/reservations");
            setItems(data || []);
        } catch (error) {
            console.warn("[reservations] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const now = Date.now();
    const upcoming = items.filter(i => new Date(i.reservation_time).getTime() >= now - 12 * 3600 * 1000 && i.status !== "completed" && i.status !== "cancelled");
    const past = items.filter(i => !upcoming.includes(i));

    const cancel = async (id) => {
        try {
            await api.post(`/reservations/${id}/cancel`);
            toast.success("Reservation cancelled");
            load();
        } catch (error) {
            console.warn("[reservations] cancel failed", error?.message);
            toast.error("Could not cancel");
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
            <h1 className="font-display text-3xl mb-1">Your reservations</h1>
            <p className="text-sm text-muted-foreground mb-6">Manage your upcoming dinners and revisit past evenings.</p>
            <Tabs defaultValue="upcoming" data-testid="reservations-tabs">
                <TabsList className="mb-4">
                    <TabsTrigger value="upcoming" data-testid="reservations-tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
                    <TabsTrigger value="past" data-testid="reservations-tab-past">Past ({past.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="upcoming">
                    <List items={upcoming} loading={loading} onCancel={cancel} onReload={load} navigate={navigate} />
                </TabsContent>
                <TabsContent value="past">
                    <List items={past} loading={loading} navigate={navigate} pastList />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function List({ items, loading, onCancel, navigate, onReload, pastList }) {
    if (loading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={`skel-${i}`} className="h-32 w-full" />)}
            </div>
        );
    }
    if (!items.length) {
        return (
            <Card><CardContent className="p-10 text-center">
                <Calendar className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                <p className="font-medium">No {pastList ? "past" : "upcoming"} reservations</p>
                <p className="text-sm text-muted-foreground">{pastList ? "Once you dine, they'll appear here." : "Find your next dining spot."}</p>
                {!pastList && <Button className="mt-4" onClick={() => navigate("/")}>Browse restaurants <ArrowRight className="h-4 w-4 ml-2" /></Button>}
            </CardContent></Card>
        );
    }
    return (
        <div className="space-y-3">
            {items.map(r => (
                <Card key={r.id} className="overflow-hidden" data-testid="reservation-list-item">
                    <div className="flex flex-col sm:flex-row">
                        <div className="sm:w-48 h-32 sm:h-auto relative">
                            <img src={r.restaurant?.image_url} alt={r.restaurant?.name} className="absolute inset-0 h-full w-full object-cover" />
                        </div>
                        <CardContent className="p-4 flex-1">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-display text-xl leading-tight">{r.restaurant?.name}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{r.restaurant?.cuisine} · {r.restaurant?.address}</div>
                                </div>
                                <Badge variant="outline" className={`capitalize border ${statusColor(r.status)}`} data-testid="reservation-status-badge">{r.status.replace("_", " ")}</Badge>
                            </div>
                            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
                                <div className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {formatDateTime(r.reservation_time)}</div>
                                <div className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {r.party_size} guests</div>
                                {r.notes && <div className="truncate max-w-[280px]">“{r.notes}”</div>}
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {!pastList && (
                                    <Button onClick={() => navigate(`/dining/${r.id}`)} data-testid="open-dining-session-button">
                                        Open dining session <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                )}
                                {pastList && r.status === "completed" && (
                                    <Button variant="secondary" onClick={() => navigate(`/dining/${r.id}`)} data-testid="view-receipt-button">View details</Button>
                                )}
                                {!pastList && r.status === "booked" && (
                                    <>
                                        <ModifyDialog reservation={r} onUpdated={onReload} />
                                        <Button variant="outline" onClick={() => onCancel(r.id)} data-testid="cancel-reservation-button">
                                            <Ban className="h-4 w-4 mr-1" /> Cancel
                                        </Button>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </div>
                </Card>
            ))}
        </div>
    );
}

function ModifyDialog({ reservation, onUpdated }) {
    const [open, setOpen] = useState(false);
    const [party, setParty] = useState(reservation.party_size);
    const dt = new Date(reservation.reservation_time);
    const initialTime = `${dt.getHours().toString().padStart(2, "0")}:${dt.getMinutes().toString().padStart(2, "0")}`;
    const [time, setTime] = useState(initialTime);

    const save = async () => {
        const newDt = new Date(reservation.reservation_time);
        const [hh, mm] = time.split(":").map(Number);
        newDt.setHours(hh, mm, 0, 0);
        try {
            await api.patch(`/reservations/${reservation.id}`, {
                party_size: party,
                reservation_time: newDt.toISOString(),
            });
            toast.success("Reservation updated");
            setOpen(false);
            onUpdated?.();
        } catch (error) {
            console.warn("[reservations] modify failed", error?.message);
            toast.error("Could not update");
        }
    };

    const TIMES = [];
    for (let h = 11; h <= 22; h++) {
        TIMES.push(`${h.toString().padStart(2, "0")}:00`);
        TIMES.push(`${h.toString().padStart(2, "0")}:30`);
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="secondary" data-testid="modify-reservation-button"><Pencil className="h-4 w-4 mr-1" /> Modify</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Modify reservation</DialogTitle>
                    <DialogDescription>Update time and party size.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div className="grid gap-1.5">
                        <Label>Time</Label>
                        <Select value={time} onValueChange={setTime}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-1.5">
                        <Label>Party size</Label>
                        <Select value={party.toString()} onValueChange={(v) => setParty(parseInt(v, 10))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={save} data-testid="modify-reservation-save">Save changes</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
